import { Box } from '@lifeomic/chroma-react/components/Box';
import React, { CSSProperties } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { ExecutiveSummaryData } from './executive-summary';
import { ICON_MAP } from '../../../AITemplates/ImpressionTypeFilter';
import { CheckCircle } from '@lifeomic/chromicons';
import { alpha } from '@mui/material/styles';
import { ViewMode } from '../AITools/Attachments/InlineAttachment';
import { useDisableAnimations } from '../../../../hooks';

const messages = defineMessages({
  intro1: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.intro',
    defaultMessage: 'Your Personalized {membershipTier} Journey',
  },
  intro2: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.intro2',
    defaultMessage: 'Evaluation & Recommendations',
  },
  intro3: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.intro3',
    defaultMessage: 'Prepared for:',
  },
  evaluationDate: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.evaluationDate',
    defaultMessage: 'Evaluation Date: {evaluationDate} | Age: {age}',
  },
  overview: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.overview',
    defaultMessage: 'Overview',
  },
  brain: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.brain',
    defaultMessage: 'Brain Health',
  },
  cancer: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.cancer',
    defaultMessage: 'Cancer Screening',
  },
  heart: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.heart',
    defaultMessage: 'Heart Health',
  },
  metabolic: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.metabolic',
    defaultMessage: 'Metabolic Health',
  },
  musculoskeletal: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.musculoskeletal',
    defaultMessage: 'Musculoskeletal Health',
  },
  nutrition: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.nutrition',
    defaultMessage: 'Nutrition Plan',
  },
  supplements: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.supplements',
    defaultMessage: 'Supplement Recommendations',
  },
  synopsis: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.synopsis',
    defaultMessage: 'Biomarker Synopsis',
  },
  actionItems: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.actionItems',
    defaultMessage: 'Action Plan',
  },
  labResults: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.labResults',
    defaultMessage: 'Lab Results',
  },
});

const useStyles = makeStyles<{
  viewMode: ViewMode;
  disableAnimations: boolean;
}>(theme => ({
  content: {
    alignItems: 'center',
    height: '100%',
    marginTop: theme.spacing(3),
    position: 'relative',
  },
  '@keyframes fadeIn': {
    '0%': {
      transform: 'scale3d(0.75, 0.75, 0.75)',
      opacity: 0,
    },
    '100%': {
      transform: 'scale3d(1, 1, 1)',
      opacity: 1,
    },
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(120px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  gradientBox: ({ disableAnimations }) => ({
    // Disable scale animation on Windows to prevent crashes
    animation: disableAnimations ? 'none' : '$fadeIn 0.3s ease-out both',
    backgroundColor: '#105dac',
    backgroundImage: `radial-gradient(
      farthest-side at top right,
      #112a70, 
      transparent
    ),
    radial-gradient(
      farthest-corner at bottom left,
      #08a5d2,
      transparent 65%
    )`,
    borderRadius: '50%',
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  }),
  iconBox: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '50%',
    padding: theme.spacing(4),
  },
  icon: {
    color: theme.palette.common.white,
    height: theme.pxToRem(60),
    width: theme.pxToRem(60),
    strokeWidth: 0.85,
  },
  intro1: ({ viewMode, disableAnimations }) => ({
    ...theme.typography.h4,
    color: theme.palette.common.white,
    // Disable animations on Windows or when printing
    animation:
      viewMode === 'print' || disableAnimations
        ? 'none'
        : '$fadeInUp 0.2s ease-out 0.15s both',
  }),
  intro2: ({ viewMode, disableAnimations }) => ({
    ...theme.typography.h1,
    color: theme.palette.common.white,
    maxWidth: '80%',
    textAlign: 'center',
    // Disable animations on Windows or when printing
    animation:
      viewMode === 'print' || disableAnimations
        ? 'none'
        : '$fadeInUp 0.2s ease-out 0.15s both',
  }),
  intro3: {
    ...theme.typography.h6,
    color: theme.palette.common.white,
  },
  findings: {
    marginTop: theme.spacing(4),
    maxWidth: '45%',
  },
  finding: ({ disableAnimations }) => ({
    ...theme.typography.body1,
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(4),
    // Disable expensive staggered animations on Windows
    // This was generating 50 CSS rules dynamically and causing crashes
    ...(disableAnimations
      ? {
          opacity: 1, // No animation, just show
        }
      : {
          opacity: 0,
          animation: '$fadeInUp 0.4s ease-out forwards',
          // Generate staggered delays only when animations are enabled
          ...(() => {
            const styles: Record<string, CSSProperties> = {};
            // Limit to 20 items instead of 50 to reduce load
            for (let i = 0; i < 20; i++) {
              styles[`&:nth-child(${i + 1})`] = {
                animationDelay: `${0.2 + i * 0.1}s`,
              };
            }
            return styles;
          })(),
        }),
  }),
  checkmark: {
    color: alpha(theme.palette.green[500], 0.8),
    minHeight: theme.pxToRem(24),
    minWidth: theme.pxToRem(24),
  },
  findingText: {
    fontSize: theme.pxToRem(20),
    color: alpha(theme.palette.common.white, 0.8),
  },
}));

export const CoverPage = ({
  patient,
  executiveSummary,
  impressionType,
  viewMode,
}: {
  patient: fhir.Patient;
  executiveSummary: ExecutiveSummaryData;
  impressionType?: string;
  viewMode: ViewMode;
}) => {
  const { formatMessage } = useIntl();
  const disableAnimations = useDisableAnimations();
  const classes = useStyles({ viewMode, disableAnimations });
  const ImpressionIcon = impressionType
    ? ICON_MAP[impressionType as keyof typeof ICON_MAP]
    : null;

  const membershipTier = patient.meta?.tag
    ?.find(
      tag =>
        tag.system === 'http://lifeomic.com/fhir/fountain-life-membership-tier',
    )
    ?.code?.toUpperCase();

  if (!executiveSummary) {
    return null;
  }

  if (impressionType && !messages[impressionType as keyof typeof messages]) {
    console.warn(`No message found for impression type: ${impressionType}`);
  }

  // Find the current impression's findings
  const currentImpression = executiveSummary.impressions.find(
    imp => imp.impressionType === impressionType,
  );

  return (
    <Box direction="column" gap={1} className={classes.content}>
      {impressionType && ImpressionIcon ? (
        <Box className={classes.gradientBox}>
          <Box className={classes.iconBox}>
            <ImpressionIcon className={classes.icon} />
          </Box>
        </Box>
      ) : (
        <Text className={classes.intro1}>
          {formatMessage(messages.intro1, {
            membershipTier,
          })}
        </Text>
      )}
      <Text className={classes.intro2}>
        {impressionType && messages[impressionType as keyof typeof messages]
          ? formatMessage(messages[impressionType as keyof typeof messages], {
              impressionType,
            })
          : formatMessage(messages.intro2)}
      </Text>

      {currentImpression?.findings && currentImpression.findings.length > 0 && (
        <Box direction="column" className={classes.findings} gap={1}>
          {currentImpression.findings.map((finding, index) => (
            <Box
              key={`${impressionType}-${index}`}
              className={classes.finding}
              gap={1}
            >
              <CheckCircle className={classes.checkmark} />
              <Text className={classes.findingText}>{finding}</Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
